import { useMemo, useState } from "react";
import Swal from "sweetalert2";
import {
  AdminButton,
  AdminField,
  AdminInput,
  AdminSelect,
  AdminTextarea,
} from "./AdminSectionCard";
import {
  useCreateManualRegistrationMutation,
  useUpdateContestRegistrationMutation,
  useDeleteContestRegistrationMutation,
} from "../../features/api/appApi";

export const STATUS_OPTIONS = ["Pending verification", "Verified", "Rejected"];
export const TSHIRT_SIZES = ["S", "M", "L", "XL", "XXL", "XXXL"];

export const statusTone = (status) =>
  status === "Verified" ? "green" : status === "Rejected" ? "red" : "amber";

export const sortedParticipants = (reg) =>
  [...(reg?.participants || [])].sort((a, b) => (a.memberOrder || 0) - (b.memberOrder || 0));

const emptyParticipant = () => ({ name: "", studentId: "", email: "", phone: "", department: "", tshirtSize: "" });

export const errMsg = (err) => err?.data?.message || err?.error || "Something went wrong.";

const errorToast = Swal.mixin({
  toast: true,
  position: "top-end",
  showConfirmButton: false,
  timer: 5000,
  timerProgressBar: true,
  didOpen: (el) => {
    el.addEventListener("mouseenter", Swal.stopTimer);
    el.addEventListener("mouseleave", Swal.resumeTimer);
  },
});

export const showErrorToast = (message) => errorToast.fire({ icon: "error", title: message });

const errorInputClass = "border-rose-400 focus:border-rose-500 focus:ring-rose-100";

function FieldError({ message }) {
  if (!message) return null;
  return <p className="mt-1 text-xs font-semibold text-rose-600">{message}</p>;
}

const validateParticipantFields = (p) => {
  const errors = {};
  if (!p.name.trim()) errors.name = "Name is required.";
  if (!p.studentId.trim()) errors.studentId = "Student ID is required.";
  if (!p.email.trim()) errors.email = "Email is required.";
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(p.email.trim())) errors.email = "Enter a valid email address.";
  if (!p.phone.trim()) errors.phone = "Phone number is required.";
  if (!p.department.trim()) errors.department = "Department is required.";
  return errors;
};

export const validateParticipantList = (participants) => {
  const list = participants.map(validateParticipantFields);
  const fieldLabel = { email: "email", studentId: "student ID", phone: "phone number" };
  ["email", "studentId", "phone"].forEach((field) => {
    const seen = new Map();
    participants.forEach((p, idx) => {
      const value = p[field].trim().toLowerCase();
      if (!value || list[idx][field]) return;
      if (seen.has(value)) {
        const firstIdx = seen.get(value);
        const message = `This ${fieldLabel[field]} is already used in another row.`;
        list[idx][field] = message;
        list[firstIdx][field] = message;
      } else {
        seen.set(value, idx);
      }
    });
  });
  return list;
};

export function ParticipantRow({ participant, label, onChange, onRemove, removable, errors = {} }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
      <div className="mb-3 flex items-center justify-between">
        <span className="text-xs font-bold uppercase tracking-wide text-slate-500">{label}</span>
        {removable ? (
          <button
            type="button"
            onClick={onRemove}
            className="text-xs font-semibold text-red-600 hover:text-red-700"
          >
            Remove
          </button>
        ) : null}
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <AdminField label="Name">
          <AdminInput
            value={participant.name}
            onChange={(e) => onChange({ ...participant, name: e.target.value })}
            className={errors.name ? errorInputClass : ""}
          />
          <FieldError message={errors.name} />
        </AdminField>
        <AdminField label="Student ID">
          <AdminInput
            value={participant.studentId}
            onChange={(e) => onChange({ ...participant, studentId: e.target.value })}
            className={errors.studentId ? errorInputClass : ""}
          />
          <FieldError message={errors.studentId} />
        </AdminField>
        <AdminField label="Email">
          <AdminInput
            type="email"
            value={participant.email}
            onChange={(e) => onChange({ ...participant, email: e.target.value })}
            className={errors.email ? errorInputClass : ""}
          />
          <FieldError message={errors.email} />
        </AdminField>
        <AdminField label="Phone">
          <AdminInput
            value={participant.phone}
            onChange={(e) => onChange({ ...participant, phone: e.target.value })}
            className={errors.phone ? errorInputClass : ""}
          />
          <FieldError message={errors.phone} />
        </AdminField>
        <AdminField label="Department">
          <AdminInput
            value={participant.department}
            onChange={(e) => onChange({ ...participant, department: e.target.value })}
            className={errors.department ? errorInputClass : ""}
          />
          <FieldError message={errors.department} />
        </AdminField>
        <AdminField label="T-shirt Size">
          <AdminSelect
            value={participant.tshirtSize || ""}
            onChange={(e) => onChange({ ...participant, tshirtSize: e.target.value })}
          >
            <option value="">—</option>
            {TSHIRT_SIZES.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </AdminSelect>
        </AdminField>
      </div>
    </div>
  );
}

export function AddRegistrationModal({ contest, onClose, onCreated }) {
  const isTeam = contest.type === "team";
  const minSize = isTeam ? contest.minTeamSize || 2 : 1;
  const maxSize = isTeam ? contest.maxTeamSize || minSize : 1;

  const [teamName, setTeamName] = useState("");
  const [reference, setReference] = useState("");
  const [participants, setParticipants] = useState(() =>
    Array.from({ length: minSize }, () => emptyParticipant())
  );
  const [paymentMethod, setPaymentMethod] = useState("");
  const [paymentNumber, setPaymentNumber] = useState("");
  const [paymentTransactionId, setPaymentTransactionId] = useState("");
  const [error, setError] = useState("");
  const [teamNameError, setTeamNameError] = useState("");
  const [participantErrors, setParticipantErrors] = useState([]);
  const [paymentErrors, setPaymentErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const [createManualRegistration] = useCreateManualRegistrationMutation();

  const updateParticipant = (idx, next) =>
    setParticipants((prev) => prev.map((p, i) => (i === idx ? next : p)));

  const addParticipant = () =>
    setParticipants((prev) => (prev.length >= maxSize ? prev : [...prev, emptyParticipant()]));

  const removeParticipant = (idx) =>
    setParticipants((prev) => (prev.length <= minSize ? prev : prev.filter((_, i) => i !== idx)));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setTeamNameError("");
    setPaymentErrors({});

    const nextParticipantErrors = validateParticipantList(participants);
    setParticipantErrors(nextParticipantErrors);
    const hasParticipantErrors = nextParticipantErrors.some((row) => Object.keys(row).length > 0);

    const nextTeamNameError = isTeam && !teamName.trim() ? "Team name is required." : "";
    setTeamNameError(nextTeamNameError);

    const nextPaymentErrors = {};
    if (contest.requiresPayment) {
      if (!paymentMethod.trim()) nextPaymentErrors.paymentMethod = "Payment method is required.";
      if (!paymentNumber.trim()) nextPaymentErrors.paymentNumber = "Sender number is required.";
      if (!paymentTransactionId.trim()) nextPaymentErrors.paymentTransactionId = "Transaction ID is required.";
      setPaymentErrors(nextPaymentErrors);
    }

    if (hasParticipantErrors || nextTeamNameError || Object.keys(nextPaymentErrors).length > 0) {
      showErrorToast("Please fix the errors below before submitting.");
      return;
    }

    setSubmitting(true);
    try {
      const body = {
        participants: participants.map((p) => ({
          name: p.name.trim(),
          studentId: p.studentId.trim(),
          email: p.email.trim(),
          phone: p.phone.trim(),
          department: p.department.trim(),
          tshirtSize: p.tshirtSize.trim(),
        })),
      };
      if (isTeam) body.teamName = teamName.trim();
      if (reference.trim()) body.reference = reference.trim();
      if (contest.requiresPayment) {
        body.paymentMethod = paymentMethod.trim();
        body.paymentNumber = paymentNumber.trim();
        body.paymentTransactionId = paymentTransactionId.trim();
      }

      const res = await createManualRegistration({ contestId: contest.id, ...body }).unwrap();
      onCreated(res.message || "Registration created successfully!");
    } catch (err) {
      const msg = errMsg(err);
      setError(msg);
      showErrorToast(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm">
      <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-2xl border border-slate-100 bg-white p-6 shadow-xl">
        <div className="flex items-center justify-between border-b border-slate-100 pb-4">
          <h3 className="text-lg font-bold text-slate-800">Add Registration (Manual / Walk-in)</h3>
          <button
            onClick={onClose}
            type="button"
            className="text-lg text-slate-400 transition-colors hover:text-slate-600"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="mt-5 space-y-5">
          {error ? (
            <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm font-medium text-rose-800">
              {error}
            </div>
          ) : null}

          {isTeam ? (
            <AdminField label="Team Name">
              <AdminInput
                value={teamName}
                onChange={(e) => {
                  setTeamName(e.target.value);
                  if (teamNameError) setTeamNameError("");
                }}
                className={teamNameError ? errorInputClass : ""}
                placeholder="Enter team name"
              />
              <FieldError message={teamNameError} />
            </AdminField>
          ) : null}

          <AdminField label="Reference (optional)">
            <AdminInput
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="e.g. Facebook, a friend, poster, teacher"
            />
          </AdminField>

          <div className="space-y-3">
            {participants.map((p, idx) => (
              <ParticipantRow
                key={idx}
                participant={p}
                label={isTeam ? (idx === 0 ? "Team Leader" : `Member ${idx + 1}`) : "Entrant"}
                onChange={(next) => {
                  updateParticipant(idx, next);
                  if (participantErrors[idx] && Object.keys(participantErrors[idx]).length > 0) {
                    setParticipantErrors((prev) => prev.map((row, i) => (i === idx ? {} : row)));
                  }
                }}
                onRemove={() => removeParticipant(idx)}
                removable={isTeam && participants.length > minSize}
                errors={participantErrors[idx]}
              />
            ))}
          </div>

          {isTeam ? (
            <div className="flex items-center justify-between">
              <span className="text-xs text-slate-500">
                Team size: {participants.length} (min {minSize}, max {maxSize})
              </span>
              <AdminButton
                type="button"
                variant="secondary"
                onClick={addParticipant}
                disabled={participants.length >= maxSize}
                className="h-9 px-3 text-xs"
              >
                + Add Member
              </AdminButton>
            </div>
          ) : null}

          {contest.requiresPayment ? (
            <div>
              <h4 className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-400">
                Payment Details (required)
              </h4>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <AdminField label="Method">
                  <AdminInput
                    value={paymentMethod}
                    onChange={(e) => {
                      setPaymentMethod(e.target.value);
                      if (paymentErrors.paymentMethod) setPaymentErrors((prev) => ({ ...prev, paymentMethod: "" }));
                    }}
                    className={paymentErrors.paymentMethod ? errorInputClass : ""}
                  />
                  <FieldError message={paymentErrors.paymentMethod} />
                </AdminField>
                <AdminField label="Sender Number">
                  <AdminInput
                    value={paymentNumber}
                    onChange={(e) => {
                      setPaymentNumber(e.target.value);
                      if (paymentErrors.paymentNumber) setPaymentErrors((prev) => ({ ...prev, paymentNumber: "" }));
                    }}
                    className={paymentErrors.paymentNumber ? errorInputClass : ""}
                  />
                  <FieldError message={paymentErrors.paymentNumber} />
                </AdminField>
                <AdminField label="Transaction ID">
                  <AdminInput
                    value={paymentTransactionId}
                    onChange={(e) => {
                      setPaymentTransactionId(e.target.value);
                      if (paymentErrors.paymentTransactionId) setPaymentErrors((prev) => ({ ...prev, paymentTransactionId: "" }));
                    }}
                    className={paymentErrors.paymentTransactionId ? errorInputClass : ""}
                  />
                  <FieldError message={paymentErrors.paymentTransactionId} />
                </AdminField>
              </div>
            </div>
          ) : null}

          <div className="flex justify-end gap-3 border-t border-slate-100 pt-4">
            <AdminButton type="button" variant="secondary" onClick={onClose} disabled={submitting}>
              Cancel
            </AdminButton>
            <AdminButton type="submit" disabled={submitting}>
              {submitting ? "Creating..." : "Create Registration"}
            </AdminButton>
          </div>
        </form>
      </div>
    </div>
  );
}

export function ManageRegistrationModal({ contest, registration, onClose, onUpdated, onDeleted }) {
  const isTeam = contest.type === "team";
  const initialParticipants = useMemo(() => sortedParticipants(registration), [registration]);

  const [teamName, setTeamName] = useState(registration.teamName || "");
  const [reference, setReference] = useState(registration.reference || "");
  const [status, setStatus] = useState(registration.status);
  const [remarks, setRemarks] = useState(registration.remarks || "");
  const [paymentMethod, setPaymentMethod] = useState(registration.paymentMethod || "");
  const [paymentNumber, setPaymentNumber] = useState(registration.paymentNumber || "");
  const [paymentTransactionId, setPaymentTransactionId] = useState(registration.paymentTransactionId || "");
  const [participants, setParticipants] = useState(
    initialParticipants.map((p) => ({ ...p, tshirtSize: p.tshirtSize || "" }))
  );
  const [error, setError] = useState("");
  const [teamNameError, setTeamNameError] = useState("");
  const [participantErrors, setParticipantErrors] = useState([]);
  const [paymentErrors, setPaymentErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [updateContestRegistration] = useUpdateContestRegistrationMutation();
  const [deleteContestRegistration] = useDeleteContestRegistrationMutation();

  const updateParticipant = (idx, field, value) => {
    setParticipants((prev) => prev.map((p, i) => (i === idx ? { ...p, [field]: value } : p)));
    if (participantErrors[idx]?.[field]) {
      setParticipantErrors((prev) =>
        prev.map((row, i) => (i === idx ? { ...row, [field]: "" } : row))
      );
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setTeamNameError("");
    setPaymentErrors({});

    const nextParticipantErrors = validateParticipantList(participants);
    setParticipantErrors(nextParticipantErrors);
    const hasParticipantErrors = nextParticipantErrors.some((row) => Object.keys(row).length > 0);

    const nextTeamNameError = isTeam && !teamName.trim() ? "Team name is required." : "";
    setTeamNameError(nextTeamNameError);

    const nextPaymentErrors = {};
    if (contest.requiresPayment) {
      if (!paymentMethod.trim()) nextPaymentErrors.paymentMethod = "Payment method is required.";
      if (!paymentNumber.trim()) nextPaymentErrors.paymentNumber = "Sender number is required.";
      if (!paymentTransactionId.trim()) nextPaymentErrors.paymentTransactionId = "Transaction ID is required.";
      setPaymentErrors(nextPaymentErrors);
    }

    if (hasParticipantErrors || nextTeamNameError || Object.keys(nextPaymentErrors).length > 0) {
      showErrorToast("Please fix the errors below before submitting.");
      return;
    }

    setSaving(true);
    try {
      const changedParticipants = participants
        .filter((p, idx) => {
          const orig = initialParticipants[idx];
          if (!orig) return false;
          return (
            p.name !== orig.name ||
            p.studentId !== orig.studentId ||
            p.email !== orig.email ||
            p.phone !== orig.phone ||
            p.department !== orig.department ||
            p.tshirtSize !== (orig.tshirtSize || "")
          );
        })
        .map((p) => ({
          id: p.id,
          name: p.name,
          studentId: p.studentId,
          email: p.email,
          phone: p.phone,
          department: p.department,
          tshirtSize: p.tshirtSize,
        }));

      const body = { status, remarks, reference: reference.trim() };
      if (isTeam) body.teamName = teamName;
      if (contest.requiresPayment) {
        body.paymentMethod = paymentMethod;
        body.paymentNumber = paymentNumber;
        body.paymentTransactionId = paymentTransactionId;
      }
      if (changedParticipants.length > 0) body.participants = changedParticipants;

      const res = await updateContestRegistration({
        contestId: contest.id,
        registrationId: registration.id,
        ...body,
      }).unwrap();
      onUpdated(res.message || "Registration updated successfully!");
    } catch (err) {
      const msg = errMsg(err);
      setError(msg);
      showErrorToast(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    const label = registration.teamName
      ? `team "${registration.teamName}" (${registration.registrationCode})`
      : `registration ${registration.registrationCode}`;
    const confirmed = await Swal.fire({
      icon: "warning",
      title: "This is a risky admin action",
      html: `You are about to permanently delete <b>${label}</b> and all of its participant data. This cannot be undone. Continue?`,
      showCancelButton: true,
      confirmButtonText: "Yes, delete it",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#64748b",
      focusCancel: true,
    });
    if (!confirmed.isConfirmed) return;
    setDeleting(true);
    setError("");
    try {
      await deleteContestRegistration({ contestId: contest.id, registrationId: registration.id }).unwrap();
      onDeleted("Registration deleted successfully!");
    } catch (err) {
      const msg = errMsg(err);
      setError(msg);
      showErrorToast(msg);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm">
      <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-2xl border border-slate-100 bg-white p-6 shadow-xl">
        <div className="flex items-center justify-between border-b border-slate-100 pb-4">
          <div>
            <h3 className="text-lg font-bold text-slate-800">
              {registration.teamName || registration.registrationCode}
            </h3>
            <p className="text-xs text-slate-400">
              {registration.registrationCode} · {registration.source === "admin" ? "Added by admin" : "Public"}
            </p>
          </div>
          <button
            onClick={onClose}
            type="button"
            className="text-lg text-slate-400 transition-colors hover:text-slate-600"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="mt-5 space-y-5">
          {error ? (
            <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm font-medium text-rose-800">
              {error}
            </div>
          ) : null}

          {isTeam ? (
            <AdminField label="Team Name">
              <AdminInput
                value={teamName}
                onChange={(e) => {
                  setTeamName(e.target.value);
                  if (teamNameError) setTeamNameError("");
                }}
                className={teamNameError ? errorInputClass : ""}
              />
              <FieldError message={teamNameError} />
            </AdminField>
          ) : null}

          <AdminField label="Reference (optional)">
            <AdminInput
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="e.g. Facebook, a friend, poster, teacher"
            />
          </AdminField>

          <div>
            <h4 className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-400">
              Participants
            </h4>
            <div className="space-y-3">
              {participants.map((p, idx) => (
                <ParticipantRow
                  key={p.id}
                  participant={p}
                  label={isTeam ? (p.role === "leader" ? "Team Leader" : "Member") : "Entrant"}
                  onChange={(next) => {
                    updateParticipant(idx, "name", next.name);
                    updateParticipant(idx, "studentId", next.studentId);
                    updateParticipant(idx, "email", next.email);
                    updateParticipant(idx, "phone", next.phone);
                    updateParticipant(idx, "department", next.department);
                    updateParticipant(idx, "tshirtSize", next.tshirtSize);
                  }}
                  removable={false}
                  errors={participantErrors[idx]}
                />
              ))}
            </div>
          </div>

          {contest.requiresPayment ? (
            <div>
              <h4 className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-400">
                Payment Details
              </h4>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <AdminField label="Method">
                  <AdminInput
                    value={paymentMethod}
                    onChange={(e) => {
                      setPaymentMethod(e.target.value);
                      if (paymentErrors.paymentMethod) setPaymentErrors((prev) => ({ ...prev, paymentMethod: "" }));
                    }}
                    className={paymentErrors.paymentMethod ? errorInputClass : ""}
                  />
                  <FieldError message={paymentErrors.paymentMethod} />
                </AdminField>
                <AdminField label="Sender Number">
                  <AdminInput
                    value={paymentNumber}
                    onChange={(e) => {
                      setPaymentNumber(e.target.value);
                      if (paymentErrors.paymentNumber) setPaymentErrors((prev) => ({ ...prev, paymentNumber: "" }));
                    }}
                    className={paymentErrors.paymentNumber ? errorInputClass : ""}
                  />
                  <FieldError message={paymentErrors.paymentNumber} />
                </AdminField>
                <AdminField label="Transaction ID">
                  <AdminInput
                    value={paymentTransactionId}
                    onChange={(e) => {
                      setPaymentTransactionId(e.target.value);
                      if (paymentErrors.paymentTransactionId) setPaymentErrors((prev) => ({ ...prev, paymentTransactionId: "" }));
                    }}
                    className={paymentErrors.paymentTransactionId ? errorInputClass : ""}
                  />
                  <FieldError message={paymentErrors.paymentTransactionId} />
                </AdminField>
              </div>
            </div>
          ) : null}

          <div className="grid grid-cols-1 gap-4 border-t border-slate-100 pt-4 md:grid-cols-2">
            <AdminField label="Status">
              <AdminSelect value={status} onChange={(e) => setStatus(e.target.value)}>
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </AdminSelect>
            </AdminField>
            <AdminField label="Remarks / Feedback">
              <AdminTextarea
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                placeholder="Add remarks or reasons if rejecting..."
                className="min-h-11"
              />
            </AdminField>
          </div>

          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
            <p className="text-sm text-slate-700">
              <span className="font-semibold">Participant self-edit is controlled contest-wide: </span>
              {contest.editingEnabled ? (
                <span className="font-semibold text-emerald-600">currently unlocked for all registrations</span>
              ) : (
                <span className="font-semibold text-slate-500">currently locked for all registrations</span>
              )}
              .
            </p>
            <p className="mt-1 text-xs text-slate-500">
              Toggle it for the whole contest from the Contests list (Edit → "Allow participant self-edit").
              Payment and status can never be changed from the public site regardless of this setting.
            </p>
          </div>

          <div className="flex justify-end gap-3">
            <AdminButton type="button" variant="danger" onClick={handleDelete} disabled={saving || deleting}>
              {deleting ? "Deleting..." : "Delete Registration"}
            </AdminButton>
            <AdminButton type="button" variant="secondary" onClick={onClose} disabled={saving || deleting}>
              Cancel
            </AdminButton>
            <AdminButton type="submit" disabled={saving || deleting}>
              {saving ? "Saving..." : "Save Changes"}
            </AdminButton>
          </div>
        </form>
      </div>
    </div>
  );
}
